import React, { useEffect, useRef, useState } from "react";
import {
  Card,
  CardHeader,
  CardBody,
  CardFooter,
  Row,
  Col,
  FormGroup,
  Label,
  Input,
  Button,
} from "reactstrap";
import Select, { components } from "react-select";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { customStyle } from "components/Common/constants";
import { JobPriorityOptions } from "components/Common/constants";
import NotificationAlert from "react-notification-alert";
import {
  renderPriority,
  formatDate,
  formatDateTime,
} from "components/Common/commonFunctions";
import { useDispatch, useSelector } from "react-redux";
import { customerList } from "constants/customer";
import { jobTypeList } from "constants/jobType";
import { getTechniciansList } from "actions/technicians";
import { getCustomerList } from "actions/customer";
import { getCustomerLocation, addJob } from "actions/job";
import { getJobTypeList } from "actions/jobType";
import {
  addCustomerEquipment,
  getCustomerEquipmentList,
} from "actions/customerEquipment";
import CommonEquipmentForm from "pages/Customers/CommonEquipmentForm";
import { formatDateWithTZ, getOneHourAheadTime } from "components/Common/commonFunctions";
import { WORK_ORDER, SMALL_CASE_WORK_ORDER } from "components/Common/constants";

const newEquiFields = {
  manufacturer: { value: null, error: "" },
  category: { value: null, error: "" },
  product: { value: null, error: "" },
  model: { value: null, error: "" },
  serialNo: { value: "", error: "" },
  assetTag: { value: "", error: "" },
  seller: { value: null, error: "" },
  purchaseDate: { value: "", error: "" },
  warranty: { value: false, error: "" },
  standardWarranty: { value: false, error: "" },
  warrantyStartDate: { value: "", error: "" },
  warrantyEndDate: { value: "", error: "" },
};

const initialFields = {
  customer: {
    value: "",
    error: "",
  },
  location: {
    value: "",
    error: "",
  },
  jobType: {
    value: "",
    error: "",
  },
  equipments: {
    value: "",
    error: "",
  },
  scheduledFrom: {
    value: null,
    error: "",
  },
  scheduledTo: {
    value: null,
    error: "",
  },
  technician: {
    value: "",
    error: "",
  },
  jobPriority: {
    value: "",
    error: "",
  },
  clientNote: {
    value: "",
    error: "",
  },
};
export const JobSlotCard = ({ selectedSlot, onClose, updatePage, region }) => {
  const dispatch = useDispatch();
  const technicianListData = useSelector(
    (state) => state.technicians.techniciansList,
  );
  const jobTypeListData = useSelector((state) => state.jobType.jobTypeList);
  const customerLocationListData = useSelector(
    (state) => state.customerLocationList.customerLocation,
  );

  const customerListData = useSelector((state) => state.customer.customerList);
  const customerEquipmentListData = useSelector(
    (state) => state.customerEquipmentList?.customerEquipment,
  );
  const notificationAlertRef = useRef(null);
  const [newJob, setNewJob] = useState(initialFields);
  const [showAddEquipmentCard, setShowAddEquipmentCard] = useState(false);
  const [jobFormData, setJobFormData] = useState(newEquiFields);
  const [showLoaderOnClick, setShowLoaderOnClick] = useState(false);

  useEffect(() => {
    let filterJSON = [
      {
        field: "Status",
        condition: "eq",
        filterValues: ["ACTIVE"],
      },
    ];
    let userFilterJSON = [
      {
        field: "RegionID",
        condition: "eq",
        filterValues: [String(region)],
      },
      {
        field: "UserStatus",
        condition: "eq",
        filterValues: ["ACTIVE"],
      },
    ];
    let technicianFilterJSON = [
      {
        field: "RegionID",
        condition: "eq",
        filterValues: [String(region)],
      },
      {
        field: "UserStatus",
        condition: "eq",
        filterValues: ["ACTIVE"],
      },
    ];
    getTechniciansList(dispatch, {
      filters: JSON.stringify(technicianFilterJSON),
    });
    getCustomerList(dispatch, { filters: JSON.stringify(userFilterJSON) });
    getJobTypeList(dispatch, { filters: JSON.stringify(filterJSON) });

    return () => {
      dispatch({ type: jobTypeList, payload: {} });
      dispatch({ type: customerList, payload: {} });
      // dispatch({ type: techniciansList, payload: {} });
    };
  }, [dispatch]);

  useEffect(() => {
    if (!showAddEquipmentCard) {
      setJobFormData(structuredClone(newEquiFields));
    }
  }, [showAddEquipmentCard]);

  useEffect(() => {
    //if slot is not selected or technician -> return
    if (!selectedSlot || !technicianListData?.data?.technician?.length) return;

    const findTechnicianById = (id) =>
      technicianListData.data.technician.find((t) => t.ID === id); //find or match technician id

    // edit mode
    if (selectedSlot.edit && selectedSlot.job) {
      const job = selectedSlot.job;

      // Use selected date if available, otherwise use today's date
      const baseDate = selectedSlot.currentDate
        ? new Date(selectedSlot.currentDate)
        : new Date();

      const startTime = new Date(baseDate);
      // here startTime if not exists  -> set basic date format  (0 0 0 0) => (hr , min ,sec ,milisec)
      startTime.setHours(job.startTime ?? 0, 0, 0, 0);

      const endTime = new Date(baseDate);
      //if endTime is not present add default 2 hr
      endTime.setHours(job.endTime ?? job.startTime + 2, 0, 0, 0);

      // If end time is earlier than start time → move to next day
      if (endTime <= startTime) endTime.setDate(endTime.getDate() + 1);

      const selectedTechnician = findTechnicianById(job.technician?.id);

      setNewJob({
        customer: { value: job.customer?.id || "", error: "" },
        location: { value: job.location?.id || "", error: "" },
        jobType: { value: job.type?.value || "", error: "" },
        clientNote: { value: job.description || "", error: "" },
        equipments: { value: job.equipments?.id || "", error: "" },

        technician: {
          value: selectedTechnician?.ID || "",
          label: selectedTechnician?.fullName || "",
          error: "",
        },

        scheduledFrom: { value: startTime, error: "" },
        scheduledTo: { value: endTime, error: "" },
        jobPriority: { value: job.priority || "", error: "" },
      });
    }

    // TIMELINE / DAY MODE
    else if (selectedSlot.view === "Timeline" || selectedSlot.view === "Day") {
      const baseDate = selectedSlot.currentDate
        ? new Date(selectedSlot.currentDate)
        : new Date();

      //extract hrs from startTime
      const [startHour] = (selectedSlot.time?.startTime || "00:00:00")
        .split(":") //["09", "30", "00"]
        .map(Number); // all are string -> convert to number

      //extract hrs from endTime
      const [endHour] = (selectedSlot.time?.endTime || "02:00:00")
        .split(":")
        .map(Number);

      // Create start time using extracted hour
      const startTime = new Date(baseDate);
      startTime.setHours(startHour, 0, 0, 0); // extract time (starthr , min ,sec ,milisec)

      // Create end time using extracted hour
      const endTime = new Date(baseDate);
      endTime.setHours(endHour, 0, 0, 0);

      if (endTime <= startTime) endTime.setDate(endTime.getDate() + 1);

      // Find selected technician from slot
      const selectedTechnician = findTechnicianById(selectedSlot.tech?.ID);

      setNewJob((prev) => ({
        ...prev,
        scheduledFrom: { value: startTime, error: "" },
        scheduledTo: { value: endTime, error: "" },

        technician: {
          value: selectedTechnician?.ID || selectedSlot.tech?.ID || null,
          label:
            selectedTechnician?.fullName || selectedSlot.tech?.fullName || "",
          error: "",
        },
      }));
    }
  }, [selectedSlot, technicianListData]);

  if (!selectedSlot) return null;

  const equipmentOptions =
    customerEquipmentListData?.data?.CustomerEquipment?.map((e) => ({
      value: e.ID,
      label: `${e?.product?.modelNo} (serial no - ${e.serialNo})` || "No Model",
      modelNo: e?.product?.modelNo,
    })) || [];

  const notify = (type, message) => {
    let finalMessage = "Something went wrong";

    if (typeof message === "string") {
      finalMessage = message;
    } else if (Array.isArray(message)) {
      finalMessage = message.map((err) => err?.Msg).join(", ");
    } else if (typeof message === "object" && message !== null) {
      finalMessage = message.Msg || JSON.stringify(message);
    }

    const options = {
      place: "tr",
      message: <div>{finalMessage}</div>,
      type,
      icon: "tim-icons icon-bell-55",
      autoDismiss: 5,
    };

    notificationAlertRef.current.notificationAlert(options);
  };

  //handle change for select
  const handleChange = async (fieldName, selectedOption) => {
    setNewJob((prev) => {
      const updated = {
        ...prev,
        [fieldName]: { value: selectedOption, error: "" },
      };

      // Reset dependent fields
      if (fieldName === "customer") {
        updated.location = { value: null, error: "" };
        updated.equipments = { value: null, error: "" };
      }

      if (fieldName === "location") {
        updated.equipments = { value: null, error: "" };
      }

      return updated;
    });

    if (fieldName === "customer" && selectedOption?.ID) {
      try {
        let locationFilterJSON = [
          {
            field: "RegionID",
            condition: "eq",
            filterValues: [String(region)],
          },
        ];

        const res = await getCustomerLocation(
          dispatch,
          selectedOption.ID,
          undefined,
          // JSON.stringify(locationFilterJSON),
        );

        if (res?.code === 200) {
          // notify("success", res.message || "Locations fetched successfully");
          // getTechniciansList(dispatch);
        } else {
          const message = Array.isArray(res?.message)
            ? res.message
              .map((err) => (err.Msg ? err.Msg : JSON.stringify(err)))
              .join(", ")
            : res?.message || "Failed to fetch locations";

          notify("danger", message);
        }
      } catch (err) {
        notify("danger", "Failed to fetch locations");
      }
    }

    if (fieldName === "location" && selectedOption?.ID) {
      const customerID = newJob.customer.value?.ID;

      if (customerID) {
        try {
          await getCustomerEquipmentList(
            dispatch,
            customerID,
            selectedOption.ID,
          );
        } catch (err) {
          console.error("Failed to load equipments", err);
        }
      }
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setNewJob((prev) => ({
      ...prev,
      [name]: { value, error: "" },
    }));
  };

  const handleDateChange = (field, date) => {
    if (field === "scheduledFrom") {
      let updatedToDate = new Date(date);

      updatedToDate.setHours(updatedToDate.getHours() + 2);

      setNewJob((prev) => ({
        ...prev,
        scheduledFrom: {
          ...prev.scheduledFrom,
          value: date,
          error: "",
        },
        scheduledTo: {
          ...prev.scheduledTo,
          value: updatedToDate,
          error: "",
        },
      }));
    } else {
      setNewJob((prev) => ({
        ...prev,
        scheduledTo: {
          ...prev.scheduledTo,
          value: date,
          error: "",
        },
      }));
    }
    // setNewJob((prev) => ({
    //   ...prev,
    //   [field]: { value: date, error: "" },
    // }));
  };

  const handleAddEquipmentClose = () => {
    setShowAddEquipmentCard(false);
    // setJobFormData(newEquiFields);
  };

  const handleFormInputChange = (e) => {
    const { name, type, checked, value } = e.target;

    setJobFormData((prev) => {
      let updated = {
        ...prev,
        [name]: { value: type === "checkbox" ? checked : value, error: "" },
      };
      if (name === "warranty" && !checked) {
        updated.standardWarranty = { value: false, error: "" };
        updated.warrantyStartDate = { value: "", error: "" };
        updated.warrantyEndDate = { value: "", error: "" };
      }

      if (name === "standardWarranty" && checked) {
        let endDate = "";
        const start = prev.warrantyStartDate.value;

        if (start) {
          const d = new Date(start);
          d.setFullYear(d.getFullYear() + 2);

          const yyyy = d.getFullYear();
          const mm = String(d.getMonth() + 1).padStart(2, "0");
          const dd = String(d.getDate()).padStart(2, "0");

          endDate = `${yyyy}-${mm}-${dd}`;
        }

        updated.warrantyEndDate = { value: endDate, error: "" };
      }

      if (name === "warrantyStartDate" && prev.standardWarranty.value) {
        let endDate = "";

        if (value) {
          const d = new Date(value);
          d.setFullYear(d.getFullYear() + 2);

          const yyyy = d.getFullYear();
          const mm = String(d.getMonth() + 1).padStart(2, "0");
          const dd = String(d.getDate()).padStart(2, "0");

          endDate = `${yyyy}-${mm}-${dd}`;
        }

        updated.warrantyEndDate = { value: endDate, error: "" };
      }

      return updated;
    });
  };

  const handleFormSelectChange = (name, option) => {
    setJobFormData((prev) => {
      const updated = {
        ...prev,
        [name]: { value: option, error: "" },
      };

      if (name === "manufacturer") {
        updated.category = { value: null, error: "" };
        updated.product = { value: null, error: "" };
        updated.model = { value: null, error: "" };
      }

      if (name === "category") {
        updated.product = { value: null, error: "" };
        updated.model = { value: null, error: "" };
      }

      if (name === "product") {
        updated.model = {
          value: option?.modelNo
            ? { value: option.modelNo, label: option.modelNo }
            : null,
          error: "",
        };
      }

      return updated;
    });
  };

  const getValidationError = (name, value) => {
    switch (name) {
      case "customer":
        return !value ? "Please select a customer" : "";
      case "location":
        return !value ? "Please select a location" : "";
      case "jobType":
        return !value ? `Please select a ${SMALL_CASE_WORK_ORDER} type` : "";
      case "equipments":
        return !value ? "Please select a equipment" : "";
      case "technician":
        return !value ? "Please select a technician" : "";
      case "jobPriority":
        return !value ? `Please select a ${SMALL_CASE_WORK_ORDER} priority` : "";
      case "clientNote":
        return !value
          ? "Please describe your issue if any "
          : // : value.length < 100
          // ? "the length of issue must be of 100 words"
          "";

      default:
        return "";
    }
  };
  //validate the data
  const validateForm = () => {
    let newJobData = { ...newJob };

    //get all the keys in array
    Object.keys(newJobData).forEach((key) => {
      newJobData[key] = {
        ...newJobData[key],
        error: getValidationError(key, newJobData[key].value),
      };
    });
    setNewJob(newJobData); //if error set error state
    return Object.values(newJobData).every((item) => item.error === "");
  };

  const validateJobEquipment = () => {
    let isValid = true;
    const updated = { ...jobFormData };

    const required = ["manufacturer", "category", "product", "seller", "model"];

    required.forEach((field) => {
      if (!jobFormData[field].value) {
        updated[field].error = `Please select ${field}`;
        isValid = false;
      } else {
        updated[field].error = "";
      }
    });

    if (
      !jobFormData.serialNo.value ||
      jobFormData.serialNo.value.trim() === ""
    ) {
      updated.serialNo.error = "Please enter serial no";
      isValid = false;
    } else {
      updated.serialNo.error = "";
    }

    if (jobFormData.warranty.value) {
      if (!jobFormData.warrantyStartDate.value) {
        updated.warrantyStartDate.error = "Start date is required";
        isValid = false;
      }
      if (
        !jobFormData.standardWarranty.value &&
        !jobFormData.warrantyEndDate.value
      ) {
        updated.warrantyEndDate.error = "End date is required";
        isValid = false;
      }
    }

    setJobFormData(updated);
    return isValid;
  };

  const handleAddEquipmentSave = async () => {
    if (!validateJobEquipment()) return;

    const payload = {
      customerId: Number(newJob.customer.value?.ID),
      customerLocationId: Number(newJob.location.value?.ID),
      manufacturerId: Number(jobFormData.manufacturer.value?.ID),
      productCategoryId: Number(jobFormData.category.value?.ID),
      productId: Number(jobFormData.product.value?.ID),
      //modelId: jobFormData.model.value?.value,
      purchaseDate: formatDateWithTZ(jobFormData.purchaseDate.value),
      serialNo: jobFormData.serialNo.value,
      assetTag: jobFormData.assetTag.value,
      seller: jobFormData.seller.value?.value,
      status: "ACTIVE",
      //warranty: jobFormData.warranty.value,
      //standardWarranty: jobFormData.standardWarranty.value,
      warrantyStartDate: formatDateWithTZ(jobFormData.warrantyStartDate.value),
      warrantyEndDate: formatDateWithTZ(jobFormData.warrantyEndDate.value),
    };

    const res = await addCustomerEquipment(payload);

    if (res?.code === 200) {
      notify("success", res?.message || "Equipment added successfully");

      const customerID = newJob.customer.value?.ID;
      const locationID = newJob.location.value?.ID;

      await getCustomerEquipmentList(dispatch, customerID, locationID);

      // Get updated list from Redux
      const updatedList = customerEquipmentListData?.data?.CustomerEquipment;

      const topItem = updatedList.length > 0 ? updatedList[0] : null;

      if (topItem) {
        setNewJob((prev) => ({
          ...prev,
          equipments: {
            value: {
              label: topItem.serialNo,
              value: topItem.ID,
              serialNo: topItem.serialNo,
            },
            error: "",
          },
        }));
      }

      handleAddEquipmentClose();
    } else {
      notify("danger", res.message || "Failed to add equipment");
    }
  };

  const handleSaveClick = async () => {
    if (!validateForm()) return;

    try {
      setShowLoaderOnClick(true);

      const bodyData = {
        customerID: newJob.customer.value?.ID,
        customerLocationID: newJob.location.value?.ID,
        customerEquipmentID: Number(newJob.equipments.value?.value),
        jobTypeID: newJob.jobType.value?.ID,
        technicianID: newJob.technician.value,
        priority: newJob.jobPriority.value?.value,
        clientNote: newJob.clientNote.value,
        jobDate: formatDate(selectedSlot?.currentDate),
        scheduledFrom: formatDateTime(newJob.scheduledFrom.value),
        scheduledTo: formatDateTime(newJob.scheduledTo.value),
      };

      const res = await addJob(bodyData);

      if (res?.code === 200) {
        notify("success", res.message || `New ${SMALL_CASE_WORK_ORDER} added successfully`);
        updatePage?.();
        setTimeout(() => {
          onClose();
        }, 2000);
      } else {
        const message = Array.isArray(res?.message)
          ? res.message.map((err) => err.Msg || JSON.stringify(err)).join(", ")
          : res?.message || `Failed to add new ${SMALL_CASE_WORK_ORDER}`;

        notify("danger", message);
      }
    } catch (error) {
      notify("danger", `Failed to add new ${SMALL_CASE_WORK_ORDER}`);
    } finally {
      setShowLoaderOnClick(false);
    }
  };

  const PriorityOption = (props) => (
    <components.Option {...props}>
      {renderPriority(props.data.value)}
    </components.Option>
  );

  const SingleValuePriority = (props) => (
    <components.SingleValue {...props}>
      {renderPriority(props.data.value)}
    </components.SingleValue>
  );

  //on blur to get if any validation error exits
  const handleOnBlur = (name, value) => {
    const trimmedValue = typeof value === "string" ? value.trim() : value;
    const error = getValidationError(name, trimmedValue);
    setNewJob((prev) => ({
      ...prev,
      [name]: {
        ...prev[name],
        value: trimmedValue,
        error,
      },
    }));
  };
  return (
    <>
      <NotificationAlert ref={notificationAlertRef} />

      <div className="job-slot-backdrop" onClick={onClose}></div>

      <div className={`job-slot-card-container`}>
        <Card className={`mb-0 job-slot-card`}>
          <CardHeader className="d-flex align-items-center justify-content-between">
            <h4 className="mb-0">
              {selectedSlot.edit ? "Edit " + WORK_ORDER : "Add New " + WORK_ORDER}
            </h4>
            <i className="fa fa-close text-muted pointer" onClick={onClose} />
          </CardHeader>
          <hr />

          <CardBody
            className={`main-content ${showAddEquipmentCard ? "blurred" : ""}`}
          >
            <Row>
              <Col md="6" className="mb-3">
                <FormGroup
                  className={`has-label ${newJob.customer.error ? "has-danger" : ""
                    } d-flex flex-column align-items-start`}
                >
                  <Label className="fw-semibold">
                    Customer <span className="validation-error">*</span>
                  </Label>
                  <Select
                    className={`text-left ${newJob.customer.error ? "is-invalid" : ""
                      }`}
                    options={customerListData?.data?.customer}
                    placeholder="Select customer"
                    styles={customStyle}
                    value={newJob.customer.value || null}
                    getOptionLabel={(option) => {
                        const fullName = `${option?.firstName || ""} ${option?.lastName || ""}`.trim();

                        return fullName
                          ? `${option?.companyName || ""} - ${fullName}`
                          : option?.companyName || "";
                      }}
                    getOptionValue={(options) => options.ID}
                    onChange={(selected) => handleChange("customer", selected)}
                    onBlur={() =>
                      handleOnBlur("customer", newJob.customer.value)
                    }
                    menuPortalTarget={document.body}
                    menuPosition={"fixed"}
                  />
                  {newJob.customer.error && (
                    <small className="validation-error">
                      {newJob.customer.error}
                    </small>
                  )}
                </FormGroup>
              </Col>

              {/* Note: Location list will be available based on selected Customer */}
              <Col md="6" className="mb-3">
                <FormGroup
                  className={`has-label ${newJob.location.error ? "has-danger" : ""
                    } d-flex flex-column align-items-start`}
                >
                  <Label className="fw-semibold">
                    Location <span className="validation-error">*</span>
                  </Label>
                  <Select
                    className={`text-left ${newJob.location.error ? "is-invalid" : ""
                      }`}
                    options={customerLocationListData?.data?.customerLocation}
                    placeholder="Select location"
                    styles={customStyle}
                    value={newJob.location.value || null}
                    getOptionLabel={(options) => options.outletName}
                    getOptionValue={(options) => options.ID}
                    onChange={(selected) => handleChange("location", selected)}
                    onBlur={() =>
                      handleOnBlur("location", newJob.location.value)
                    }
                    menuPortalTarget={document.body}
                    menuPosition={"fixed"}
                    isDisabled={!newJob.customer.value}
                  />
                  {newJob.location.error && (
                    <small className="validation-error">
                      {newJob.location.error}
                    </small>
                  )}
                </FormGroup>
              </Col>

              {/* Note: Equipment list will be available based on selected Customer and Location */}
              <Col md="6" className="mb-3">
                <FormGroup>
                  <Label className="fw-semibold">
                    Equipment <span className="validation-error">*</span>
                  </Label>
                  <Select
                    className={`text-left ${newJob.equipments.error ? "is-invalid" : ""
                      }`}
                    placeholder="Select equipment"
                    styles={customStyle}
                    options={equipmentOptions}
                    value={newJob.equipments.value || null}
                    onChange={(selected) =>
                      handleChange("equipments", selected)
                    }
                    onBlur={() =>
                      handleOnBlur("equipments", newJob.equipments.value)
                    }
                    menuPortalTarget={document.body}
                    menuPosition={"fixed"}
                    isDisabled={
                      !newJob.customer.value || !newJob.location.value
                    }
                  />
                  {newJob.equipments.error && (
                    <small className="validation-error">
                      {newJob.equipments.error}
                    </small>
                  )}
                  {/* {selectedSlot?.edit && ( */}
                  <fieldset
                    disabled={!newJob.customer.value || !newJob.location.value}
                  >
                    <Button
                      color="link"
                      className="p-0 m-0 btn-sm align-baseline"
                      onClick={() => setShowAddEquipmentCard(true)}
                    >
                      <i className="mt-3 fa fa-plus fa-sm" /> Add New Equipment
                    </Button>
                  </fieldset>
                  {/* )} */}
                </FormGroup>
              </Col>

              <Col md="6" className="mb-3">
                <FormGroup
                  className={`has-label ${newJob.jobType.error ? "has-danger" : ""
                    } d-flex flex-column align-items-start`}
                >
                  <Label className="fw-semibold">
                    {WORK_ORDER} Type <span className="validation-error">*</span>
                  </Label>
                  <Select
                    className={`text-left ${newJob.jobType.error ? "is-invalid" : ""
                      }`}
                    options={jobTypeListData?.data?.jobType}
                    placeholder={`Select ${SMALL_CASE_WORK_ORDER} type`}
                    styles={customStyle}
                    value={newJob.jobType.value || null}
                    getOptionLabel={(options) => options.name}
                    getOptionValue={(options) => options.ID}
                    onChange={(selected) => handleChange("jobType", selected)}
                    onBlur={() => handleOnBlur("jobType", newJob.jobType.value)}
                    menuPortalTarget={document.body}
                    menuPosition={"fixed"}
                  />
                  {newJob.jobType.error && (
                    <small className="validation-error">
                      {newJob.jobType.error}
                    </small>
                  )}
                </FormGroup>
              </Col>

              <Col md="6" className="mb-3">
                <FormGroup
                  className={`has-label ${newJob.scheduledFrom.error ? "has-danger" : ""
                    } d-flex flex-column align-items-start`}
                >
                  <Label className="fw-semibold">
                    Scheduled From <span className="validation-error">*</span>
                  </Label>
                  <DatePicker
                    selected={newJob.scheduledFrom.value}
                    onChange={(date) => handleDateChange("scheduledFrom", date)}
                    placeholderText={`Select ${SMALL_CASE_WORK_ORDER} start date & time`}
                    showTimeSelect
                    timeFormat="hh:mm aa"
                    timeIntervals={30}
                    dateFormat="dd MMM yyyy, hh:mm aa"
                    className="form-control cust-datepicker"
                    showIcon
                    toggleCalendarOnIconClick
                    icon={<i className="fa-solid fa-calendar-day fa-md mt-1" />}
                    wrapperClassName="w-100"
                  />
                  {newJob.scheduledFrom.error && (
                    <small className="validation-error">
                      {newJob.scheduledFrom.error}
                    </small>
                  )}
                </FormGroup>
              </Col>

              <Col md="6" className="mb-3">
                <FormGroup
                  className={`has-label ${newJob.scheduledTo.error ? "has-danger" : ""
                    } d-flex flex-column align-items-start`}
                >
                  <Label className="fw-semibold">
                    Scheduled To <span className="validation-error">*</span>
                  </Label>
                  <DatePicker
                    selected={newJob.scheduledTo.value}
                    onChange={(date) => handleDateChange("scheduledTo", date)}
                    placeholderText={`Select ${SMALL_CASE_WORK_ORDER} end date & time`}
                    showTimeSelect
                    timeFormat="hh:mm aa"
                    timeIntervals={30}
                    dateFormat="dd MMM yyyy, hh:mm aa"
                    className="form-control cust-datepicker"
                    showIcon
                    toggleCalendarOnIconClick
                    icon={<i className="fa-solid fa-calendar-day fa-md mt-1" />}
                    wrapperClassName="w-100"
                    minDate={newJob.scheduledFrom.value}
                    maxDate={newJob.scheduledFrom.value}
                    minTime={getOneHourAheadTime(newJob.scheduledFrom.value)}
                    maxTime={new Date().setHours(23, 59, 59, 999)}
                  />
                  {newJob.scheduledTo.error && (
                    <small className="validation-error">
                      {newJob.scheduledTo.error}
                    </small>
                  )}
                </FormGroup>
              </Col>

              <Col md="6" className="mb-3">
                <FormGroup
                  className={`has-label ${newJob.clientNote.error ? "has-danger" : ""
                    } d-flex flex-column align-items-start`}
                >
                  <Label className="fw-semibold">
                    Client-Reported Problem{" "}
                    <span className="validation-error">*</span>
                  </Label>
                  <Input
                    name="clientNote"
                    type="textarea"
                    rows="2"
                    placeholder="Describe the problem reported by client"
                    value={newJob.clientNote.value}
                    onChange={handleInputChange}
                    onBlur={(e) => handleOnBlur(e.target.name, e.target.value)}
                    maxLength={2000}
                  />
                  {newJob.clientNote.error && (
                    <small className="validation-error">
                      {newJob.clientNote.error}
                    </small>
                  )}
                </FormGroup>
              </Col>

              <Col md="6" className="mb-3">
                <FormGroup
                  className={`has-label ${newJob.technician.error ? "has-danger" : ""
                    } d-flex flex-column align-items-start`}
                >
                  <Label className="fw-semibold">
                    Technician <span className="validation-error">*</span>
                  </Label>
                  <Select
                    classNamePrefix={`react-select ${newJob.technician.error ? "is-invalid" : ""
                      }`}
                    options={technicianListData?.data?.technician || []}
                    placeholder="Select technician"
                    styles={customStyle}
                    value={
                      technicianListData?.data?.technician?.find(
                        (t) => t.ID === newJob.technician.value,
                      ) || null
                    }
                    getOptionLabel={(options) => options.fullName}
                    getOptionValue={(options) => options.ID}
                    onChange={(selected) =>
                      handleChange("technician", selected?.ID)
                    }
                    onBlur={() =>
                      handleOnBlur("technician", newJob.technician.value)
                    }
                    menuPortalTarget={document.body}
                    menuPosition={"fixed"}
                  />
                  {newJob.technician.error && (
                    <small className="validation-error">
                      {newJob.technician.error}
                    </small>
                  )}
                </FormGroup>
              </Col>

              <Col md="6" className="mb-3">
                <FormGroup
                  className={` has-label ${newJob.jobPriority.error ? "has-danger" : ""
                    } d-flex flex-column align-items-start`}
                >
                  <Label className="fw-semibold">
                    {WORK_ORDER} Priority <span className="validation-error">*</span>
                  </Label>
                  <Select
                    className={`text-left ${newJob.jobPriority.error ? "is-invalid" : ""
                      }`}
                    options={JobPriorityOptions}
                    placeholder={`Select ${SMALL_CASE_WORK_ORDER} priority`}
                    styles={customStyle}
                    components={{
                      Option: PriorityOption,
                      SingleValue: SingleValuePriority,
                    }}
                    value={newJob.jobPriority.value || null}
                    getOptionLabel={(options) => options.label}
                    getOptionValue={(options) => options.value}
                    onChange={(selected) =>
                      handleChange("jobPriority", selected)
                    }
                    onBlur={() =>
                      handleOnBlur("jobPriority", newJob.jobPriority.value)
                    }
                    menuPortalTarget={document.body}
                    menuPosition={"fixed"}
                  />
                  {newJob.jobPriority.error && (
                    <small className="validation-error">
                      {newJob.jobPriority.error}
                    </small>
                  )}
                </FormGroup>
              </Col>
            </Row>
          </CardBody>

          <CardFooter
            className={`main-content ${showAddEquipmentCard ? "blurred" : ""
              } d-flex justify-content-end mb-0`}
          >
            <Button color="secondary" onClick={onClose} className="mr-2">
              Cancel
            </Button>
            <Button
              color="default"
              type="submit"
              onClick={() => {
                handleSaveClick();
              }}
              disabled={Object.values(newJob).some((item) => item.error !== "")}
            >
              {showLoaderOnClick ? (
                <i className="fa fa-spinner fa-spin fa-lg text-white" />
              ) : (
                "Save"
              )}
            </Button>
          </CardFooter>
        </Card>
      </div>

      {showAddEquipmentCard && (
        <>
          <div
            className="job-slot-backdrop"
            onClick={handleAddEquipmentClose}
          ></div>

          <div className="equipment-card-container">
            <Card className="add-equipment-card">
              <CardHeader
                className="d-flex align-items-center justify-content-between"
                tag="h4"
              >
                Add New Equipment
                <i
                  className="fa fa-close text-muted pointer"
                  onClick={handleAddEquipmentClose}
                />
              </CardHeader>

              <hr />

              <CardBody>
                <CommonEquipmentForm
                  formData={jobFormData}
                  handleSelectChange={handleFormSelectChange}
                  handleInputChange={handleFormInputChange}
                  handleEquipmentBlur={() => { }} // optional for now
                />
              </CardBody>

              <CardFooter className="d-flex justify-content-end mb-0">
                <Button
                  color="secondary"
                  onClick={handleAddEquipmentClose}
                  className="mr-2"
                >
                  Cancel
                </Button>
                <Button
                  color="default"
                  type="submit"
                  onClick={handleAddEquipmentSave}
                >
                  Save
                </Button>
              </CardFooter>
            </Card>
          </div>
        </>
      )}
    </>
  );
};
